import { useEffect, useCallback } from 'react'
import { useVideo } from '@mc/video'

/**
 * Retries autoplay when the document gains focus or becomes visible.
 *
 * The shared VideoAutoplay checks document.hasFocus() once when the video
 * becomes ready. On a fresh page load this check often fails because focus
 * hasn't been established yet, and VideoAutoplay never retries. This
 * component listens for focus/visibility events and retries the play call.
 *
 * Place as a child of <Video> to access the video context.
 */
export const AutoplayOnView = () => {
  const { play, state, readyState, source } = useVideo()

  const attemptPlay = useCallback(() => {
    if (document.visibilityState !== 'visible') return
    play({ autoPlay: true }).catch(() => {})
  }, [play])

  useEffect(() => {
    if (!source || readyState !== 'ready' || state !== 'idle') return undefined

    attemptPlay()
    document.addEventListener('visibilitychange', attemptPlay)
    window.addEventListener('focus', attemptPlay)
    return () => {
      document.removeEventListener('visibilitychange', attemptPlay)
      window.removeEventListener('focus', attemptPlay)
    }
  }, [source, readyState, state, attemptPlay])

  return null
}
